// Re-rank a list of POIs against the user's raw NL query so nuance from the
// original phrase ("cheap", "vegetarian", "with parking", "near hotel")
// tightens the ordering AFTER the category filter has narrowed the pool.
//
// Called from NearbyMap.jsx AFTER /api/discover/nearby returns and AFTER
// /api/nearby/nl-search has produced a hint. Pure client-side, deterministic,
// no LLM roundtrip. Distance is still the dominant signal — this only breaks
// ties or nudges relevant matches up by 1-3 slots.

const STOPWORDS = new Set([
  "the","a","an","and","or","of","in","on","at","to","for","with","near","by",
  "me","my","find","show","get","list","some","any","please","around","from",
  "within","km","kms","kilometer","kilometers","kilometre","kilometres","meters","m",
]);

const CHEAP_TOKENS   = ["cheap", "budget", "affordable", "low", "value"];
const PREMIUM_TOKENS = ["premium", "luxury", "high", "upscale", "fancy"];
const VEG_TOKENS     = ["veg", "vegetarian", "vegan", "sattvic", "jain"];
const PARK_TOKENS    = ["parking", "valet"];
const AC_TOKENS      = ["ac", "aircon", "air-conditioned"];

// Extract meaningful tokens from the raw query. Removes stopwords, splits on
// non-word chars, lowercases.
function tokenize(raw) {
  if (!raw) return [];
  return raw.toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(t => t && t.length >= 3 && !STOPWORDS.has(t));
}

// Build the searchable "haystack" for a POI: name + address + category +
// tags string. Everything the user might have described.
function haystackOf(p) {
  const tagStr = Object.entries(p.tags || {})
    .map(([k, v]) => `${k} ${v}`).join(" ");
  return (
    `${p.name || p.title || ""} ${p.address || ""} ${p.category || ""} ${tagStr}`
  ).toLowerCase();
}

// Score a POI against tokens + hint. Higher = more relevant.
function scorePoi(p, tokens, hint) {
  const hay = haystackOf(p);
  let score = 0;

  // 1) Distance boost (closer = higher). Capped at +5.
  if (typeof p.distance_km === "number") {
    score += Math.max(0, 5 - p.distance_km);
  }

  // 2) Raw keyword hits (each unique token match = +1, max 4).
  const hits = new Set();
  tokens.forEach(t => { if (hay.includes(t)) hits.add(t); });
  score += Math.min(4, hits.size) * 1.0;

  // 3) Intent-flag boosts (Gemini parsed these — reward POIs that match).
  if (hint?.veg_only && VEG_TOKENS.some(t => hay.includes(t)))            score += 3;
  if (hint?.price_bias === "cheap"   && CHEAP_TOKENS.some(t => hay.includes(t)))   score += 2;
  if (hint?.price_bias === "premium" && PREMIUM_TOKENS.some(t => hay.includes(t))) score += 2;
  if (hint?.brand) {
    if (hay.includes(hint.brand.toLowerCase())) score += 4;
  }

  // 4) Conditional amenity boosts driven by tokens actually in the query.
  if (tokens.some(t => PARK_TOKENS.includes(t)) && PARK_TOKENS.some(t => hay.includes(t))) score += 2;
  if (tokens.some(t => AC_TOKENS.includes(t))   && AC_TOKENS.some(t => hay.includes(t)))   score += 1;

  // 5) Rating (if the source provided one, 0-5). Small tie-breaker.
  if (typeof p.rating === "number") score += p.rating * 0.2;

  return score;
}

export function rerankPois(pois, rawQuery, hint) {
  if (!Array.isArray(pois) || pois.length <= 1) return pois || [];
  const tokens = tokenize(rawQuery);
  // If the query is category-only ("hotels", "pharmacy"), no re-rank needed —
  // the category filter already did the work. Keep distance ordering.
  if (tokens.length === 0 && !hint?.veg_only && !hint?.price_bias && !hint?.brand) {
    return pois;
  }
  // Attach scores so downstream (dev tools / testing) can inspect.
  const scored = pois.map(p => ({ ...p, _rerank_score: scorePoi(p, tokens, hint) }));
  scored.sort((a, b) => b._rerank_score - a._rerank_score);
  return scored;
}
